import React, {
	Fragment,
	useState,
	useRef,
	useEffect,
	useContext,
} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography, OutlinedInput } from '@mui/material';

import content from './content';
import { Tooltip } from '../FT-UX';
import { Modal } from '../../../common';
import { addItemsStyles } from './styles';
import AddBulkItem from './AddBulkItem';
import CheckBoxGroup from './CheckBoxGroup';
import { colors } from '../../../theme/color-palette';
import {
	updateSelectedItem,
	getSearchItemResults,
	updateSearchItemResults,
	updateCompareSelectedItemA,
	updateCompareSelectedItemB,
} from '../../../redux/actions/createRuleActions';
import { getStoredTooltipData } from '../FT-UX/util';
import { ResultTable, SearchResult } from './SearchResult';
import { UserPortalContext } from '../../../context/appcontext';
import { removeSpecialCharacters } from '../../../helper/utils';

const AddItem = ({
	type,
	isOpen,
	setIsAddItemsOpen,
	getData,
	isComparison,
}) => {
	const t = content[type]?.add;
	const dispatch = useDispatch();
	const TYPE = type?.toLocaleLowerCase();
	const [inputQuery, setInputQuery] = useState('');
	const [searchMode, setSearchMode] = useState(false);
	const portalUserContext = useContext(UserPortalContext);
	const [isAddBulkItemOpen, setIsAddBulkItemOpen] = useState(false);
	const {
		selectedItems,
		selectedItemsA,
		selectedItemsB,
		locationCheckBox,
		productCheckBox,
	} = useSelector((state) => state.createRuleReducer);
	const { alertForAggregated, alertForIndividual } = locationCheckBox;
	const { alertForProductAggregated, alertForProductIndividual } =
		productCheckBox;
	const selectedItemsArray = selectedItems[TYPE] || [];
	const selectedItemsArrayA = selectedItemsA[TYPE] || [];
	const selectedItemsArrayB = selectedItemsB[TYPE] || [];
	const showSelectedItems =
		!searchMode &&
		(selectedItemsArray?.length ||
			selectedItemsArrayA?.length ||
			selectedItemsArrayB?.length);

	const storedValue = getStoredTooltipData();
	const [enableSubmitBtn, setEnableSubmitBtn] = useState(
		!selectedItemsArray?.length ||
			!selectedItemsArrayA?.length ||
			!selectedItemsArrayB?.length
	);
	let showTooltip;
	let tooltipType;
	if (TYPE === 'store') {
		tooltipType = 'searchStore';
		showTooltip = storedValue?.showSearchStoreTooltip;
	} else {
		tooltipType = 'searchItem';
		showTooltip = storedValue?.showSearchItemTooltip;
	}

	const debouncedSearch = useRef(
		debounce((params) => {
			if (params?.inputQuery?.length) {
				dispatch(getSearchItemResults(params));
			}
		}, 500)
	).current;

	useEffect(() => {
		getData({
			type,
			alertForAggregated:
				type === 'Store' ? alertForAggregated : alertForProductAggregated,
			alertForIndividual:
				type === 'Store' ? alertForIndividual : alertForProductIndividual,
			selectedItems: getSelectedItems(),
		});
	}, []);

	useEffect(() => {
		debouncedSearch({ type, inputQuery, portalUserContext });
	}, [inputQuery, type]);

	useEffect(() => {
		if (inputQuery?.length > 0) {
			setSearchMode(true);
		}
		if (inputQuery?.length === 0) {
			setSearchMode(false);
		}
	}, [inputQuery]);

	const onSearchInputChange = (e) => {
		const sanitzedInputText = removeSpecialCharacters(e.target.value);
		setInputQuery(sanitzedInputText);
	};

	const onClose = () => {
		setIsAddItemsOpen(false);
		clearModal();
	};

	useEffect(() => {
		setEnableSubmitBtn(
			selectedItemsArray.length ||
				selectedItemsArrayA.length ||
				selectedItemsArrayB.length
				? false
				: true
		);
	}, [selectedItemsArray, selectedItemsArrayA, selectedItemsArrayB]);

	/**
	 * Clear Search input, Remove Search result from Store
	 */
	const clearModal = () => {
		setInputQuery('');
		dispatch(updateSearchItemResults({ [TYPE]: [] }));
	};

	/**
	 * Clear Add Item Modal, open Bulk Item Modal
	 */
	const onPasteItemLinkClick = () => {
		clearModal();
		setIsAddBulkItemOpen(true);
	};

	/**
	 * Saves the Selections and Sends to Parent component via callback
	 * Clear the Search results
	 */
	const onSubmitClick = () => {
		if (searchMode) {
			setInputQuery('');
			setSearchMode(false);
			dispatch(updateSearchItemResults({ [TYPE]: [] }));
			return;
		}
		getData({
			type,
			alertForAggregated:
				type === 'Store' ? alertForAggregated : alertForProductAggregated,
			alertForIndividual:
				type === 'Store' ? alertForIndividual : alertForProductIndividual,
			selectedItems: getSelectedItems(),
		});
		onClose();
	};

	/**
	 * Clears the Selected items and
	 * Search result items
	 */
	const onCancelClickHandler = () => {
		if (searchMode) {
			setInputQuery('');
			setSearchMode(false);
			return;
		}
		onClose();
		dispatch(updateSelectedItem({ [TYPE]: [] }));
		dispatch(updateCompareSelectedItemA({ [TYPE]: [] }));
		dispatch(updateCompareSelectedItemB({ [TYPE]: [] }));
		getData({
			type,
			alertForAggregated:
				type === 'Store' ? alertForAggregated : alertForProductAggregated,
			alertForIndividual:
				type === 'Store' ? alertForIndividual : alertForProductIndividual,
			selectedItems: [],
		});
	};

	const getSelectedItems = () => {
		let selectedItems = selectedItemsArray;
		if (isComparison === 'A') {
			selectedItems = selectedItemsArrayA;
		}
		if (isComparison === 'B') {
			selectedItems = selectedItemsArrayB;
		}
		return selectedItems?.filter((item) => item.isValid || item?.checked);
	};

	if (isAddBulkItemOpen) {
		return (
			<AddBulkItem
				type={type}
				isOpen={isAddBulkItemOpen}
				isComparison={isComparison}
				setIsAddBulkItemOpen={setIsAddBulkItemOpen}
				getBulkData={(selectedItems) => {
					getData({
						type,
						alertForAggregated:
							type === 'Store' ? alertForAggregated : alertForProductAggregated,
						alertForIndividual:
							type === 'Store' ? alertForIndividual : alertForProductIndividual,
						selectedItems,
					});
				}}
			/>
		);
	}

	const actionButtons = (
		<Fragment>
			<Button
				onClick={onCancelClickHandler}
				disableRipple
				style={addItemsStyles.cancelButton}>
				Cancel
			</Button>
			<Button
				onClick={onSubmitClick}
				disabled={enableSubmitBtn}
				style={{
					...addItemsStyles.saveButton,
					backgroundColor: enableSubmitBtn ? colors.GREY : colors.PURPLE,
				}}>
				{searchMode ? 'Done' : 'Save'}
			</Button>
		</Fragment>
	);

	return (
		<Modal
			title={
				<Typography variant="h2" style={addItemsStyles.title}>
					{t?.['title']}
				</Typography>
			}
			open={isOpen}
			handleClose={onClose}
			actionButtons={actionButtons}>
			<Box style={{ display: 'flex' }}>
				{/* Search Box */}
				<Tooltip show={showTooltip} placement="bottom-end" type={tooltipType}>
					<OutlinedInput
						value={inputQuery}
						autoComplete={'off'}
						placeholder={t['inputPlaceholder']}
						sx={addItemsStyles.searchBox}
						onChange={onSearchInputChange}
						inputProps={addItemsStyles.searchBoxInput}
						startAdornment={<SearchIcon sx={addItemsStyles.searchIconColor} />}
					/>
				</Tooltip>
				{!searchMode && type !== 'Brand' ? (
					<Button
						variant="text"
						disableRipple
						onClick={onPasteItemLinkClick}
						style={addItemsStyles.pasteItemLink}>
						{t['pasteButton']}
					</Button>
				) : null}
			</Box>
			{/* Check boxes */}
			{!searchMode ? <CheckBoxGroup type={TYPE} /> : null}

			{/* Selected Items */}
			{showSelectedItems ? (
				<ResultTable
					type={TYPE}
					result={getSelectedItems()}
					isComparison={isComparison}
					setDisableDoneButton={setEnableSubmitBtn}
				/>
			) : null}

			{/* Search Results */}
			{searchMode ? (
				<SearchResult
					type={TYPE}
					isComparison={isComparison}
					setDisableDoneButton={setEnableSubmitBtn}
				/>
			) : null}
		</Modal>
	);
};

AddItem.propTypes = {
	type: PropTypes.oneOf([
		'Store',
		'Item',
		'Category',
		'Department',
		'Brand',
		'UPC',
		'Subcategory',
	]).isRequired,
	isOpen: PropTypes.bool.isRequired,
	setIsAddItemsOpen: PropTypes.func.isRequired,
	getData: PropTypes.func,
	isComparison: PropTypes.string,
};

export default AddItem;
